import { useEffect, useState, useCallback, useRef } from 'react';

// Makes the public photo-share page (/u/:slug) installable as a per-shop app.
//
// The tricky part: every shop is a different URL, but a PWA opens its manifest's
// fixed start_url. So we build the manifest ON THE FLY (a Blob) with start_url set to
// THIS shop's /u/:slug and name set to the shop — each QR then installs as its own
// one-tap icon. iOS ignores the manifest for "Add to Home Screen" (it re-opens the
// current URL) so the Apple meta tags below cover iPhone; the manifest covers Android.

const ICONS = '/pwa';
// The installed home-screen shortcut is always branded "Sohozkaj Photo Booth"
// (not the shop name).
const APP_NAME = 'Sohozkaj Photo Booth';

const ua = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');
const isIOS = () => /iPhone|iPad|iPod/i.test(ua()) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/i.test(ua());
// iOS "Add to Home Screen" only exists in Safari — not Chrome/Firefox/in-app webviews.
const isIOSSafari = () => isIOS() && /Safari/i.test(ua()) && !/CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line/i.test(ua());
const isStandalone = () =>
    (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true);

// Inject the manifest (per-shop start_url, SohozKaj name/icon) + Apple home-screen
// meta. Returns a cleanup fn.
function injectManifestAndMeta({ slug }) {
    const origin = window.location.origin;
    const name = APP_NAME;
    const startUrl = `${origin}/u/${slug}`;
    const manifest = {
        id: `/u/${slug}`,
        name,
        short_name: name,
        start_url: startUrl,
        scope: startUrl,
        display: 'standalone',
        display_override: ['standalone', 'minimal-ui'],
        orientation: 'portrait',
        background_color: '#ffffff',
        theme_color: '#ffffff',
        icons: [
            { src: `${origin}${ICONS}/booth-icon-192.png`, sizes: '192x192', type: 'image/png', purpose: 'any' },
            { src: `${origin}${ICONS}/booth-icon-512.png`, sizes: '512x512', type: 'image/png', purpose: 'any' },
            { src: `${origin}${ICONS}/booth-icon-maskable-512.png`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
        ],
    };
    const blobUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }));

    const added = [];
    const add = (el) => { el.setAttribute('data-booth-pwa', '1'); document.head.appendChild(el); added.push(el); };
    const link = (rel, href) => { const el = document.createElement('link'); el.rel = rel; el.href = href; add(el); };
    const meta = (n, c) => { const el = document.createElement('meta'); el.setAttribute('name', n); el.setAttribute('content', c); add(el); };

    link('manifest', blobUrl);
    link('icon', `${origin}/images/favicon.svg`); // SohozKaj favicon in the browser tab
    link('apple-touch-icon', `${origin}${ICONS}/apple-touch-icon.png`);
    meta('apple-mobile-web-app-capable', 'yes');
    meta('mobile-web-app-capable', 'yes');
    meta('apple-mobile-web-app-status-bar-style', 'default');
    meta('apple-mobile-web-app-title', name);
    meta('theme-color', '#ffffff');

    return () => { added.forEach((el) => el.remove()); URL.revokeObjectURL(blobUrl); };
}

export function useBoothInstall({ slug, enabled = true }) {
    const [installed, setInstalled] = useState(() => isStandalone());
    const [hasNativePrompt, setHasNativePrompt] = useState(false);
    const deferredRef = useRef(null);

    // Wire up the manifest + meta + service worker once the booth is usable.
    useEffect(() => {
        if (!enabled || !slug || installed) return undefined;
        const cleanup = injectManifestAndMeta({ slug });
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/booth-sw.js', { scope: '/u/' }).catch(() => { /* non-fatal */ });
        }
        return cleanup;
    }, [enabled, slug, installed]);

    // Android fires beforeinstallprompt when the page is installable; stash it for the button.
    useEffect(() => {
        const onBIP = (e) => { e.preventDefault(); deferredRef.current = e; setHasNativePrompt(true); };
        const onInstalled = () => { setInstalled(true); setHasNativePrompt(false); deferredRef.current = null; };
        window.addEventListener('beforeinstallprompt', onBIP);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBIP);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    // Fire the native Android install dialog. Returns false when there's no native
    // prompt (caller then shows the manual "Add to Home Screen" steps instead).
    const promptInstall = useCallback(async () => {
        const dp = deferredRef.current;
        if (!dp) return false;
        dp.prompt();
        try { await dp.userChoice; } catch { /* ignore */ }
        deferredRef.current = null;
        setHasNativePrompt(false);
        return true;
    }, []);

    // Show the install affordance on phones (the target), or anywhere the browser has
    // actually offered a native install prompt (e.g. desktop Chrome — handy for testing).
    const canShow = enabled && !installed && (isIOSSafari() || isAndroid() || hasNativePrompt);
    const platform = isIOS() ? 'ios' : 'android';

    return { canShow, hasNativePrompt, platform, promptInstall };
}
